import React, { useContext, useState } from 'react';

import PropTypes from 'prop-types';

import { invalidatePostQueries } from 'common/actions/postQueries';
import { reloadPost } from 'common/actions/posts';
import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import connect from 'common/core/connect';
import Spinner from 'common/Spinner';
import AdminCreateAzureDevopsWorkItemModal from 'common/subdomain/admin/AdminCreateAzureDevopsWorkItemModal';
import AdminSidebarSection from 'common/subdomain/admin/AdminSidebarSection';
import Tappable from 'common/Tappable';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';

import AdminAzureDevopsSearchForm from './AdminAzureDevopsSearchForm';

import 'css/components/subdomain/admin/_AdminAzureDevopsPostSidebarSection.scss';

const AdminAzureDevopsPostSidebarSection = ({ post, reloadPost }) => {
  // context
  const company = useContext(CompanyContext);

  // state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearchFormOpen, setIsSearchFormOpen] = useState(false);
  const [removingWorkItemID, setRemovingWorkItemID] = useState(null);

  // helpers
  const onCreateWorkItem = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
  };

  const onToggleSearchForm = () => {
    setIsSearchFormOpen((state) => !state);
  };

  const removeWorkItem = async (workItem) => {
    if (removingWorkItemID === workItem.id) {
      return;
    }

    setRemovingWorkItemID(workItem.id);

    const response = await AJAX.post('/api/azureDevops/workItems/unlink', {
      workItemLinkID: workItem.linkID,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });
    if (!error) {
      await reloadPost(post);
    }

    setRemovingWorkItemID(null);
  };

  // renders
  const renderCreateButton = () => {
    return (
      <Tappable onTap={onCreateWorkItem}>
        <div className="createButton">Create new Work Item</div>
      </Tappable>
    );
  };

  const renderWorkItems = () => {
    const { azureDevopsWorkItems } = post;

    if (!azureDevopsWorkItems?.length) {
      return null;
    }

    const workItems = azureDevopsWorkItems.map((workItem) => (
      <div key={workItem.id} className="workItem">
        <a href={workItem.url} rel="noopener" target="_blank">
          {workItem.title}
        </a>
        {removingWorkItemID === workItem.id ? (
          <div className="spinnerContainer">
            <Spinner />
          </div>
        ) : (
          <Tappable onTap={() => removeWorkItem(workItem)}>
            <button className="icon icon-x" />
          </Tappable>
        )}
      </div>
    ));

    return <div className="workItems">{workItems}</div>;
  };

  const renderSearchForm = () => {
    if (!isSearchFormOpen) {
      return null;
    }

    return <AdminAzureDevopsSearchForm post={post} />;
  };

  const renderAction = () => {
    return (
      <Tappable onTap={onToggleSearchForm}>
        <button>Link Work Item</button>
      </Tappable>
    );
  };

  const render = () => {
    const doesPlanSupportIntegration = company?.integrations?.azureDevops;
    const isIntegrationActive = !!company?.activeIntegrations?.azureDevops;

    if (!doesPlanSupportIntegration || !isIntegrationActive) {
      return null;
    }

    return (
      <AdminSidebarSection
        action={renderAction()}
        className="adminAzureDevopsPostSidebarSection"
        title="DevOps">
        {renderSearchForm()}
        {renderWorkItems()}
        {renderCreateButton()}
        {isModalOpen && <AdminCreateAzureDevopsWorkItemModal post={post} onClose={onCloseModal} />}
      </AdminSidebarSection>
    );
  };

  return render();
};

AdminAzureDevopsPostSidebarSection.propTypes = {
  company: PropTypes.shape({ _id: PropTypes.string }),
  post: PropTypes.object,
};

export default connect(null, (dispatch) => ({
  reloadPost: (post) => {
    return Promise.all([dispatch(invalidatePostQueries()), dispatch(reloadPost(post))]);
  },
}))(AdminAzureDevopsPostSidebarSection);
